/**
 * Service for fetching Montreal property tax data from the Open Data API
 */

const API_URL = 'https://donnees.montreal.ca/api/3/action/datastore_search';
const API_SQL_URL = 'https://donnees.montreal.ca/api/3/action/datastore_search_sql';
const TAX_CURRENT_YEAR = 2026;
const TAX_PREVIOUS_YEAR = 2025;
const SCHOOL_PREVIOUS_VAL_YEAR = 2024;
// School tax 2025-2026 uses 2025 valuation and a rate of 0.08423 per 100$.
const SCHOOL_RATE_CURRENT = 0.08423;
// School tax 2024-2025 uses 2024 valuation and a rate of 0.09152 per 100$.
const SCHOOL_RATE_PREVIOUS = 0.09152;
const isDev = process.env.NODE_ENV !== 'production';
const debugLog = (...args) => {
  if (isDev) console.log(...args);
};

/**
 * Remove accents and special characters from a string
 * @param {string} str - String to normalize
 * @returns {string} - String without accents
 */
const removeAccents = (str) => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

const stripLettersFromCivic = (value) => {
  if (!value) return '';
  return String(value).toUpperCase().replace(/[A-Z]/g, '').replace(/\s+/g, '').trim();
};

const normalizeSuite = (value) => {
  const raw = String(value || '').toUpperCase().trim();
  if (!raw) return '';
  let normalized = raw
    .replace(/^(APPARTEMENT|APPART|APP|APT|APPT|SUITE|UNIT|UNITE|#)\s*/i, '')
    .replace(/\s+/g, '')
    .replace(/[^0-9A-Z]/g, '');
  if (/^\d+$/.test(normalized)) {
    normalized = normalized.replace(/^0+/, '');
  }
  return normalized;
};

const filterCandidatesByAppNumber = (candidates, appNumber) => {
  const target = normalizeSuite(appNumber);
  if (!target) return candidates;
  const filtered = candidates.filter((candidate) => {
    const suite = normalizeSuite(candidate.suite);
    return suite && suite === target;
  });
  return filtered.length > 0 ? filtered : candidates;
};

const summarizeCandidates = (candidates) => {
  const totals = candidates.reduce(
    (acc, candidate) => {
      acc.totalCurrent += parseFloat(candidate.municipalTaxCurrentYear ?? candidate.municipalTaxLastYear) || 0;
      acc.totalPrevious += parseFloat(candidate.municipalTaxPreviousYear ?? candidate.municipalTaxTwoYearsAgo) || 0;
      return acc;
    },
    { totalCurrent: 0, totalPrevious: 0 }
  );
  const totalTaxAmountCurrent = totals.totalCurrent.toFixed(2);
  const totalTaxAmountPrevious = totals.totalPrevious.toFixed(2);

  return {
    totalTaxAmountCurrentYear: totalTaxAmountCurrent,
    totalTaxAmountPreviousYear: totalTaxAmountPrevious,
    [`totalTaxAmount${TAX_CURRENT_YEAR}`]: totalTaxAmountCurrent,
    [`totalTaxAmount${TAX_PREVIOUS_YEAR}`]: totalTaxAmountPrevious
  };
};

const escapeSqlLiteral = (value) => String(value).replace(/'/g, "''");

const formatAddress = (record) => {
  if (!record) return '';
  const civ1 = (record.AD_EMPLAC_CIV1 || '').trim();
  const civ2 = (record.AD_EMPLAC_CIV2 || '').trim();
  const street = (record.AD_EMPLAC_RUE || '').trim();
  const suite = (record.AD_EMPLAC_SUITE1 || '').trim();
  const number = civ2 && civ2 !== civ1 ? `${civ1}-${civ2}` : civ1;
  let address = [number, street].filter(Boolean).join(' ').trim();
  if (suite) {
    address = `${address}, ${suite}`;
  }
  return address;
};

const findValImposable = (records = []) => {
  const record = records.find((entry) => parseFloat(entry.VAL_IMPOSABLE) > 0);
  return record ? parseFloat(record.VAL_IMPOSABLE) || 0 : 0;
};

const calculateSchoolTax = (valImposable, rate) => {
  if (!valImposable) return 0;
  return Math.max(0, (valImposable - 25000)) * rate / 100;
};

const logSchoolValuationInputs = ({ accountId, address, valuation2024, valuation2025 }) => {
  console.info(
    `[Loyer] Valeur fonciere pour taxe scolaire (${accountId || "compte inconnu"})`,
    {
      address: address || "",
      valImposable2024: valuation2024 || 0,
      valImposable2025: valuation2025 || 0
    }
  );
};

const buildCandidateData = ({
  sampleRecord,
  accountId,
  municipalCurrent,
  municipalPrevious,
  schoolCurrent,
  schoolPrevious
}) => ({
  address: formatAddress(sampleRecord),
  suite: (sampleRecord.AD_EMPLAC_SUITE1 || '').trim(),
  propertyId: sampleRecord.CV_IDENT_PROP || '',
  municipalTaxCurrentYear: municipalCurrent.toFixed(2),
  municipalTaxPreviousYear: municipalPrevious.toFixed(2),
  schoolTaxCurrentYear: schoolCurrent.toFixed(2),
  schoolTaxPreviousYear: schoolPrevious.toFixed(2),
  // Legacy keys used by the 2026 form mapping.
  municipalTaxLastYear: municipalCurrent.toFixed(2),
  municipalTaxTwoYearsAgo: municipalPrevious.toFixed(2),
  schoolTaxAmount2025: schoolCurrent.toFixed(2),
  schoolTaxAmount2024: schoolPrevious.toFixed(2),
  taxId: sampleRecord.NO_COMPTE || accountId,
  taxYear: `${TAX_CURRENT_YEAR}/${TAX_PREVIOUS_YEAR}`
});

const fetchAddressCandidates = async ({ resourceId, streetNumber, streetName, year }) => {
  if (!resourceId || !streetNumber || !streetName) return [];

  const baseNumber = stripLettersFromCivic(streetNumber);
  if (!baseNumber) return [];

  const normalizedStreetName = removeAccents(String(streetName).trim().toUpperCase());
  const escapedStreet = escapeSqlLiteral(normalizedStreetName);
  const escapedNumber = escapeSqlLiteral(baseNumber);
  const yearValue = Number(year);

  const sql = `
    SELECT DISTINCT "NO_COMPTE", "AD_EMPLAC_CIV1", "AD_EMPLAC_CIV2", "AD_EMPLAC_RUE"
    FROM "${resourceId}"
    WHERE "AD_EMPLAC_RUE" = '${escapedStreet}'
      AND "ANNEE_EXERCICE" = ${Number.isNaN(yearValue) ? TAX_CURRENT_YEAR : yearValue}
      AND regexp_replace(regexp_replace("AD_EMPLAC_CIV1", '^0+', ''), '[A-Z]', '', 'g') = '${escapedNumber}'
    ORDER BY "AD_EMPLAC_CIV1"
  `;

  const url = `${API_SQL_URL}?sql=${encodeURIComponent(sql)}`;
  debugLog('Tax SQL URL:', url);
  const response = await fetch(url);
  const data = await response.json();
  if (!data.success) return [];

  return data.result?.records || [];
};

const fetchTaxRecords = async ({ resourceId, query, year }) => {
  const params = new URLSearchParams({
    resource_id: resourceId,
    limit: 100,
    q: query
  });
  if (year) {
    params.append('q', String(year));
  }
  const url = `${API_URL}?${params.toString()}`;
  debugLog('Tax API URL:', url);
  const response = await fetch(url);
  const data = await response.json();
  return data.result?.records || [];
};

/**
 * Search for property tax information by address
 * @param {Object} params Search parameters
 * @param {string} params.streetNumber Street number (e.g., "5757")
 * @param {string} params.streetName Street name (e.g., "HADLEY")
 * @param {string} params.resourceId Resource ID for the arrondissement (optional, defaults to RESOURCE_ID)
 * @param {string} params.appNumber Apartment/suite number (optional)
 * @returns {Promise<Object>} Tax information
 */
export const searchPropertyTax = async ({ streetNumber, streetName, resourceId, appNumber }) => {
  try {
    // Convert inputs to uppercase to match the API data format
    const normalizedStreetNumber = streetNumber ? String(streetNumber).trim() : "";
    const normalizedStreetName = streetName ? removeAccents(String(streetName).trim().toUpperCase()) : "";

    const numericStreetNumberMatch = normalizedStreetNumber.match(/\d+/);
    const numericStreetNumber = numericStreetNumberMatch ? numericStreetNumberMatch[0] : "";
    if (!numericStreetNumber || !normalizedStreetName) {
      return {
        success: false,
        error: 'Missing street number or street name',
        data: []
      };
    }

    // Always search by civic number only (no letter, no apartment).
    const addressQuery = `${numericStreetNumber} ${normalizedStreetName}`;
    debugLog('Tax address search query (numeric only):', addressQuery);

    let sqlCandidates = await fetchAddressCandidates({
      resourceId,
      streetNumber: normalizedStreetNumber,
      streetName: normalizedStreetName,
      year: TAX_CURRENT_YEAR
    });

    if (sqlCandidates.length === 0) {
      sqlCandidates = await fetchAddressCandidates({
        resourceId,
        streetNumber: normalizedStreetNumber,
        streetName: normalizedStreetName,
        year: TAX_PREVIOUS_YEAR
      });
    }

    if (sqlCandidates.length > 0) {
      const accountIds = Array.from(
        new Set(sqlCandidates.map((record) => record.NO_COMPTE).filter(Boolean))
      );

      if (accountIds.length > 0) {
        const accountResults = await Promise.all(
          accountIds.map(async (accountId) => {
            const response = await fetchPropertyTaxByAccount({
              taxAccountId: accountId,
              resourceId
            });
            if (response.success && response.data) {
              return response.data;
            }
            return null;
          })
        );

        const resolvedCandidates = accountResults.filter(Boolean);
        if (resolvedCandidates.length > 0) {
          const filteredCandidates = filterCandidatesByAppNumber(resolvedCandidates, appNumber);
          const totals = summarizeCandidates(filteredCandidates);
          return {
            success: true,
            data: filteredCandidates,
            totalResults: filteredCandidates.length,
            ...totals
          };
        }
      }
    }

    const [recordsCurrent, recordsPrevious] = await Promise.all([
      fetchTaxRecords({ resourceId, query: addressQuery, year: TAX_CURRENT_YEAR }),
      fetchTaxRecords({ resourceId, query: addressQuery, year: TAX_PREVIOUS_YEAR })
    ]);

    debugLog(`Records for ${TAX_CURRENT_YEAR}:`, recordsCurrent);

    debugLog(`Filtered records for address range (${TAX_CURRENT_YEAR}):`, recordsCurrent.length);

    if (recordsCurrent.length === 0) {
      return {
        success: false,
        error: 'No property found matching the address',
        data: []
      };
    }

    debugLog(`Records for ${TAX_PREVIOUS_YEAR}:`, recordsPrevious);

    debugLog(`Filtered records for address range (${TAX_PREVIOUS_YEAR}):`, recordsPrevious.length);

    const groupRecordsByAccount = (records) => {
      const map = new Map();
      records.forEach((record) => {
        const account = record.NO_COMPTE || 'UNKNOWN';
        if (!map.has(account)) {
          map.set(account, { total: 0, records: [], sample: record, valImposable: null });
        }
        const entry = map.get(account);
        entry.records.push(record);
        entry.total += parseFloat(record.MONTANT_DETAIL) || 0;
        if (entry.valImposable === null) {
          const val = parseFloat(record.VAL_IMPOSABLE);
          if (!Number.isNaN(val) && val > 0) {
            entry.valImposable = val;
          }
        }
      });
      return map;
    };

    // Calculate sum of MONTANT_DETAIL for current municipal year.
    let totalMontantDetailCurrent = 0;
    debugLog(`Records from ${TAX_CURRENT_YEAR}:`, recordsCurrent.length);

    totalMontantDetailCurrent = recordsCurrent.reduce((sum, record) => {
      return sum + (parseFloat(record.MONTANT_DETAIL) || 0);
    }, 0);

    debugLog(`Sum of MONTANT_DETAIL for ${TAX_CURRENT_YEAR}:`, totalMontantDetailCurrent);

    // Calculate sum of MONTANT_DETAIL for previous municipal year.
    let totalMontantDetailPrevious = 0;
    debugLog(`Records from ${TAX_PREVIOUS_YEAR}:`, recordsPrevious.length);

    totalMontantDetailPrevious = recordsPrevious.reduce((sum, record) => {
      return sum + (parseFloat(record.MONTANT_DETAIL) || 0);
    }, 0);

    debugLog(`Sum of MONTANT_DETAIL for ${TAX_PREVIOUS_YEAR}:`, totalMontantDetailPrevious);

    const accountsCurrent = groupRecordsByAccount(recordsCurrent);
    const accountsPrevious = groupRecordsByAccount(recordsPrevious);

    // Need municipal data from the prior valuation year for school-tax year alignment.
    let recordsSchoolPreviousValYear = [];
    try {
      recordsSchoolPreviousValYear = await fetchTaxRecords({
        resourceId,
        query: addressQuery,
        year: SCHOOL_PREVIOUS_VAL_YEAR
      });
      debugLog(`Records from ${SCHOOL_PREVIOUS_VAL_YEAR}:`, recordsSchoolPreviousValYear.length);
    } catch (error) {
      console.error(`Error fetching ${SCHOOL_PREVIOUS_VAL_YEAR} property tax data for school tax calculation:`, error);
    }

    const accountsSchoolPreviousValYear = groupRecordsByAccount(recordsSchoolPreviousValYear);
    const accountIds = [...new Set([...accountsCurrent.keys(), ...accountsPrevious.keys()])];

    const candidates = accountIds
      .map((accountId) => {
        const entryCurrent = accountsCurrent.get(accountId);
        const entryPrevious = accountsPrevious.get(accountId);
        if (!entryCurrent || !entryPrevious) return null;

        const sampleRecord = entryCurrent.sample || entryPrevious.sample;
        const valImposableCurrent = entryPrevious.valImposable || 0;
        const valImposablePrevious = accountsSchoolPreviousValYear.get(accountId)?.valImposable || 0;
        logSchoolValuationInputs({
          accountId,
          address: formatAddress(sampleRecord),
          valuation2024: valImposablePrevious,
          valuation2025: valImposableCurrent
        });

        const schoolTaxCurrent = calculateSchoolTax(valImposableCurrent, SCHOOL_RATE_CURRENT);
        const schoolTaxPrevious = calculateSchoolTax(valImposablePrevious, SCHOOL_RATE_PREVIOUS);

        return buildCandidateData({
          sampleRecord,
          accountId,
          municipalCurrent: entryCurrent.total,
          municipalPrevious: entryPrevious.total,
          schoolCurrent: schoolTaxCurrent,
          schoolPrevious: schoolTaxPrevious
        });
      })
      .filter(Boolean);

    if (candidates.length === 0) {
      return {
        success: false,
        error: 'No tax data found for required years',
        data: []
      };
    }

    debugLog('Final values before return:', {
      [`totalTaxAmount${TAX_CURRENT_YEAR}`]: totalMontantDetailCurrent.toFixed(2),
      [`totalTaxAmount${TAX_PREVIOUS_YEAR}`]: totalMontantDetailPrevious.toFixed(2),
      candidates: candidates.length
    });

    // Create results for each account with both tax amounts and school tax
    const filteredCandidates = filterCandidatesByAppNumber(candidates, appNumber);
    const totals = summarizeCandidates(filteredCandidates);

    return {
      success: true,
      data: filteredCandidates,
      totalResults: filteredCandidates.length,
      ...totals
    };
  } catch (error) {
    console.error('Error fetching property tax data:', error);
    debugLog('Error details:', {
      message: error.message,
      stack: error.stack,
      searchParams: { streetNumber, streetName, resourceId }
    });
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

export const fetchPropertyTaxByAccount = async ({ taxAccountId, resourceId }) => {
  try {
    if (!taxAccountId) {
      return { success: false, error: 'missing_account', data: null };
    }

    const [recordsCurrent, recordsPrevious, recordsSchoolPreviousValYear] = await Promise.all([
      fetchTaxRecords({ resourceId, query: taxAccountId, year: TAX_CURRENT_YEAR }),
      fetchTaxRecords({ resourceId, query: taxAccountId, year: TAX_PREVIOUS_YEAR }),
      fetchTaxRecords({ resourceId, query: taxAccountId, year: SCHOOL_PREVIOUS_VAL_YEAR })
    ]);

    if (!recordsCurrent.length || !recordsPrevious.length) {
      return { success: false, error: 'missing_required_years', data: null };
    }

    const totalCurrent = recordsCurrent.reduce((sum, record) => sum + (parseFloat(record.MONTANT_DETAIL) || 0), 0);
    const totalPrevious = recordsPrevious.reduce((sum, record) => sum + (parseFloat(record.MONTANT_DETAIL) || 0), 0);
    const valImposableCurrent = findValImposable(recordsPrevious);
    const valImposablePrevious = findValImposable(recordsSchoolPreviousValYear);
    const sampleRecord = recordsCurrent[0] || recordsPrevious[0];
    logSchoolValuationInputs({
      accountId: taxAccountId,
      address: formatAddress(sampleRecord),
      valuation2024: valImposablePrevious,
      valuation2025: valImposableCurrent
    });
    const schoolTaxCurrent = calculateSchoolTax(valImposableCurrent, SCHOOL_RATE_CURRENT);
    const schoolTaxPrevious = calculateSchoolTax(valImposablePrevious, SCHOOL_RATE_PREVIOUS);

    return {
      success: true,
      data: buildCandidateData({
        sampleRecord,
        accountId: taxAccountId,
        municipalCurrent: totalCurrent,
        municipalPrevious: totalPrevious,
        schoolCurrent: schoolTaxCurrent,
        schoolPrevious: schoolTaxPrevious
      })
    };
  } catch (error) {
    console.error('Error fetching property tax data by account:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};
